import React from "react";

const INTENT_THRESHOLD_PX = 8;
const HORIZONTAL_LOCK_RATIO = 1.2;
const TICKER_PIXELS_PER_SECOND = 48;

const getPrimaryAnimation = (node) => {
  if (!node || typeof node.getAnimations !== "function") return null;
  return node.getAnimations().find((animation) => animation.effect) || null;
};

const wrapTime = (value, duration) => {
  if (!Number.isFinite(duration) || duration <= 0) return Math.max(0, value);
  return ((value % duration) + duration) % duration;
};

/**
 * Mobile-only ticker drag.
 *
 * Important behavior:
 * - A normal tap never pauses or moves the ticker.
 * - A vertical gesture is immediately released to the page scroll.
 * - Only a clearly horizontal gesture locks into ticker dragging.
 * - After release, the CSS animation resumes from the dragged position.
 */
export const useTickerDrag = ({
  containerRef,
  contentRef,
  paused = false,
  enabled = true,
}) => {
  const gestureRef = React.useRef(null);
  const suppressClickRef = React.useRef(false);
  const rafRef = React.useRef(null);
  const pendingTimeRef = React.useRef(null);

  const flushAnimationTime = React.useCallback(() => {
    rafRef.current = null;
    const gesture = gestureRef.current;
    const nextTime = pendingTimeRef.current;
    if (!gesture?.animation || nextTime == null) return;

    try {
      gesture.animation.currentTime = nextTime;
    } catch (_) {}
  }, []);

  const scheduleAnimationTime = React.useCallback(
    (time) => {
      pendingTimeRef.current = time;
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(flushAnimationTime);
    },
    [flushAnimationTime]
  );

  const clearGesture = React.useCallback(() => {
    gestureRef.current = null;
    pendingTimeRef.current = null;
  }, []);

  const finishGesture = React.useCallback(
    (event) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.pointerId !== event.pointerId) return;

      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        flushAnimationTime();
      }

      if (gesture.mode === "horizontal") {
        suppressClickRef.current = true;
        window.setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);

        try {
          if (containerRef.current?.hasPointerCapture?.(event.pointerId)) {
            containerRef.current.releasePointerCapture(event.pointerId);
          }
        } catch (_) {}

        if (gesture.animation && !paused) {
          try {
            gesture.animation.play();
          } catch (_) {}
        }
      }

      clearGesture();
    },
    [clearGesture, containerRef, flushAnimationTime, paused]
  );

  React.useEffect(() => {
    if (!enabled) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const onPointerDown = (event) => {
      // Desktop/mouse keeps the exact pre-v149 ticker behavior.
      if (event.pointerType === "mouse") return;
      if (event.isPrimary === false) return;

      const animation = getPrimaryAnimation(contentRef.current);
      if (!animation) return;

      gestureRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        animation,
        mode: "pending",
        dragStartX: null,
        dragStartTime: null,
        duration: 0,
      };
    };

    const onPointerMove = (event) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.pointerId !== event.pointerId) return;

      const dxFromStart = event.clientX - gesture.startX;
      const dyFromStart = event.clientY - gesture.startY;
      const absX = Math.abs(dxFromStart);
      const absY = Math.abs(dyFromStart);

      if (gesture.mode === "pending") {
        if (absX < INTENT_THRESHOLD_PX && absY < INTENT_THRESHOLD_PX) return;

        // Vertical intent belongs to the page. We stop watching this gesture
        // without pausing animation, preventing scroll/tap interference.
        if (absY >= absX || absX < absY * HORIZONTAL_LOCK_RATIO) {
          clearGesture();
          return;
        }

        // Lock only after horizontal intent is unambiguous. Snapshot the
        // animation at this exact moment so there is no jump from touch-down.
        const animation = gesture.animation;
        const timing = animation.effect?.getComputedTiming?.();
        gesture.duration = Number(timing?.duration) || 0;
        gesture.dragStartTime = Number(animation.currentTime) || 0;
        gesture.dragStartX = event.clientX;
        gesture.mode = "horizontal";

        try {
          animation.pause();
          container.setPointerCapture?.(event.pointerId);
        } catch (_) {}
      }

      if (gesture.mode !== "horizontal") return;

      // Only horizontal ticker dragging cancels the browser gesture.
      event.preventDefault();

      const dragDx = event.clientX - gesture.dragStartX;
      const deltaTime = (dragDx / TICKER_PIXELS_PER_SECOND) * 1000;
      const nextTime = wrapTime(
        gesture.dragStartTime + deltaTime,
        gesture.duration
      );
      scheduleAnimationTime(nextTime);
    };

    const onPointerUp = (event) => finishGesture(event);
    const onPointerCancel = (event) => finishGesture(event);
    const onClickCapture = (event) => {
      if (!suppressClickRef.current) return;
      event.preventDefault();
      event.stopPropagation();
    };

    container.addEventListener("pointerdown", onPointerDown, { passive: true });
    container.addEventListener("pointermove", onPointerMove, { passive: false });
    container.addEventListener("pointerup", onPointerUp);
    container.addEventListener("pointercancel", onPointerCancel);
    container.addEventListener("click", onClickCapture, true);

    return () => {
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", onPointerUp);
      container.removeEventListener("pointercancel", onPointerCancel);
      container.removeEventListener("click", onClickCapture, true);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      clearGesture();
    };
  }, [
    clearGesture,
    containerRef,
    contentRef,
    enabled,
    finishGesture,
    scheduleAnimationTime,
  ]);
};
